/* eslint-disable max-lines */
// cspell:words geotrans snorthing seasting

// Adapted from trailbehind/leaflet-grids/blob/master/mgrs.js
import {fromUTM, toUTM, type UTMPosition} from './UTM';

const A = 65;
const I = 73;
const O = 79;
const V = 86;
const Z = 90;

// UTM zones are grouped, and assigned to one of a group of 6 sets
const NUM_100K_SETS = 6;

// The column letters (for easting) of the lower left value, per set
// cspell:disable-next-line
const SET_ORIGIN_COLUMN_LETTERS = 'AJSAJS';

/// The row letters (for northing) of the lower left value, per set
// cspell:disable-next-line
const SET_ORIGIN_ROW_LETTERS = 'AFAFAF';

/**
 * Decode the UTM parameters from a MGRS string.
 *
 * @private
 * @param {string} mgrsString an UPPERCASE coordinate string is expected.
 * @return {object} An object literal with easting, northing, zoneLetter,
 *     zoneNumber and accuracy (in meters) properties.
 */
function decode(mgrsString: string) {
    if (mgrsString && mgrsString.length === 0) {
        throw new Error('MGRSPoint converting from nothing');
    }

    let sb = '';
    let testChar;
    let i = 0;

    // get Zone number
    while (!/[A-Z]/.test((testChar = mgrsString.charAt(i)))) {
        if (i >= 2) {
            throw new Error(`MGRSPoint bad conversion from: ${mgrsString}`);
        }
        sb += testChar;
        i++;
    }

    const zoneNumber = parseInt(sb, 10);

    if (i === 0 || i + 3 > mgrsString.length) {
        // A good MGRS string has to be 4-5 digits long, ##AAA/#AAA at least.
        throw new Error(`MGRSPoint bad conversion from: ${mgrsString}`);
    }

    const zoneLetter = mgrsString.charAt(i++);
    // Should we check the zone letter here? Why not.
    if (
        zoneLetter <= 'A' ||
        zoneLetter === 'B' ||
        zoneLetter === 'Y' ||
        zoneLetter >= 'Z' ||
        zoneLetter === 'I' ||
        zoneLetter === 'O'
    ) {
        throw new Error(`MGRSPoint zone letter ${zoneLetter} not handled: ${mgrsString}`);
    }

    const hunK = mgrsString.substring(i, (i += 2));

    const set = get100kSetForZone(zoneNumber);

    const east100k = getEastingFromChar(hunK.charAt(0), set);
    let north100k = getNorthingFromChar(hunK.charAt(1), set);

    // We have a bug where the northing may be 2000000 too low.
    // How do we know when to roll over?

    while (north100k < getMinNorthing(zoneLetter)) {
        north100k += 2000000;
    }

    // calculate the char index for easting/northing separator
    const remainder = mgrsString.length - i;

    if (remainder % 2 !== 0) {
        throw new Error(`
        MGRSPoint has to have an even number
        of digits after the zone letter and two
        100km letters - front half for easting
        meters, second half for
        northing meters - ${mgrsString}
        `);
    }

    const sep = remainder / 2;

    let sepEasting = 0.0;
    let sepNorthing = 0.0;
    let accuracyBonus;
    if (sep > 0) {
        accuracyBonus = 100000.0 / Math.pow(10, sep);
        sepEasting = parseFloat(mgrsString.substring(i, i + sep)) * accuracyBonus;
        sepNorthing = parseFloat(mgrsString.substring(i + sep)) * accuracyBonus;
    }

    const easting = sepEasting + east100k;
    const northing = sepNorthing + north100k;

    return {
        easting: easting,
        northing: northing,
        zoneLetter: zoneLetter,
        zoneNumber: zoneNumber,
        accuracy: accuracyBonus,
    };
}

/**
 * Encodes a UTM location as MGRS string.
 *
 * @private
 * @param {object} utm An object literal with easting, northing,
 *     zoneLetter, zoneNumber
 * @param {number} accuracy Accuracy in digits (1-5).
 * @return {string} MGRS string for the given UTM location.
 */
function encode(utm: UTMPosition, accuracy: number) {
    // prepend with leading zeroes
    const seasting = `00000${utm.easting}`;
    const snorthing = `00000${utm.northing}`;

    return (
        String(utm.zoneNumber) +
        String(utm.zoneLetter) +
        get100kID(utm.easting, utm.northing, utm.zoneNumber) +
        seasting.substr(seasting.length - 5, accuracy) +
        snorthing.substr(snorthing.length - 5, accuracy)
    );
}

// Get the two letter 100k designator for a given UTM easting,
// northing and zone number value.
function get100kID(easting: number, northing: number, zoneNumber: number) {
    const setParm = get100kSetForZone(zoneNumber);
    const setColumn = Math.floor(easting / 100000);
    const setRow = Math.floor(northing / 100000) % 20;
    return getLetter100kID(setColumn, setRow, setParm);
}

// Given a UTM zone number, figure out the MGRS 100K set it is in.
function get100kSetForZone(i: number) {
    return i % NUM_100K_SETS === 0 ? NUM_100K_SETS : i % NUM_100K_SETS;
}

/**
 * Get the two-letter MGRS 100k designator given information
 * translated from the UTM northing, easting and zone number.
 *
 * @private
 * @param {number} column the column index as it relates to the MGRS
 *        100k set spreadsheet, created from the UTM easting.
 *        Values are 1-8.
 * @param {number} row the row index as it relates to the MGRS 100k set
 *        spreadsheet, created from the UTM northing value. Values
 *        are from 0-19.
 * @param {number} parm the set block, as it relates to the MGRS 100k set
 *        spreadsheet, created from the UTM zone. Values are from
 *        1-60.
 * @return {string} two letter MGRS 100k code.
 */
function getLetter100kID(column: number, row: number, parm: number) {
    // colOrigin and rowOrigin are the letters at the origin of the set
    const colOrigin = SET_ORIGIN_COLUMN_LETTERS.charCodeAt(parm - 1);
    const rowOrigin = SET_ORIGIN_ROW_LETTERS.charCodeAt(parm - 1);

    // colInt and rowInt are the letters to build to return
    let colInt = colOrigin + column - 1;
    let rowInt = rowOrigin + row;
    let rollover = false;

    if (colInt > Z) {
        colInt = colInt - Z + A - 1;
        rollover = true;
    }

    if (
        colInt === I ||
        (colOrigin < I && colInt > I) ||
        ((colInt > I || colOrigin < I) && rollover)
    ) {
        colInt++;
    }

    if (
        colInt === O ||
        (colOrigin < O && colInt > O) ||
        ((colInt > O || colOrigin < O) && rollover)
    ) {
        colInt++;

        if (colInt === I) {
            colInt++;
        }
    }

    if (colInt > Z) {
        colInt = colInt - Z + A - 1;
    }

    if (rowInt > V) {
        rowInt = rowInt - V + A - 1;
        rollover = true;
    } else {
        rollover = false;
    }

    if (
        rowInt === I ||
        (rowOrigin < I && rowInt > I) ||
        ((rowInt > I || rowOrigin < I) && rollover)
    ) {
        rowInt++;
    }

    if (
        rowInt === O ||
        (rowOrigin < O && rowInt > O) ||
        ((rowInt > O || rowOrigin < O) && rollover)
    ) {
        rowInt++;

        if (rowInt === I) {
            rowInt++;
        }
    }

    if (rowInt > V) {
        rowInt = rowInt - V + A - 1;
    }

    return String.fromCharCode(colInt) + String.fromCharCode(rowInt);
}

/**
 * Given the first letter from a two-letter MGRS 100k zone, and given the
 * MGRS table set for the zone number, figure out the easting value that
 * should be added to the other, secondary easting value.
 *
 * @private
 * @param {char} e The first letter from a two-letter MGRS 100´k zone.
 * @param {number} set The MGRS table set for the zone number.
 * @return {number} The easting value for the given letter and set.
 */
function getEastingFromChar(e: string, set: number) {
    // colOrigin is the letter at the origin of the set for the
    // column
    let curCol = SET_ORIGIN_COLUMN_LETTERS.charCodeAt(set - 1);
    let eastingValue = 100000.0;
    let rewindMarker = false;

    while (curCol !== e.charCodeAt(0)) {
        curCol++;
        if (curCol === I) {
            curCol++;
        }
        if (curCol === O) {
            curCol++;
        }
        if (curCol > Z) {
            if (rewindMarker) {
                throw new Error(`Bad character: ${e}`);
            }
            curCol = A;
            rewindMarker = true;
        }
        eastingValue += 100000.0;
    }

    return eastingValue;
}

/**
 * Given the second letter from a two-letter MGRS 100k zone, and given the
 * MGRS table set for the zone number, figure out the northing value that
 * should be added to the other, secondary northing value. You have to
 * remember that Northings are determined from the equator, and the vertical
 * cycle of letters mean a 2000000 additional northing meters. This happens
 * approx. every 18 degrees of latitude. This method does *NOT* count any
 * additional northings. You have to figure out how many 2000000 meters need
 * to be added for the zone letter of the MGRS coordinate.
 *
 * @private
 * @param {char} n Second letter of the MGRS 100k zone
 * @param {number} set The MGRS table set number, which is dependent on the
 *     UTM zone number.
 * @return {number} The northing value for the given letter and set.
 */
function getNorthingFromChar(n: string, set: number) {
    if (n > 'V') {
        throw new Error(`MGRSPoint given invalid Northing: ${n}`);
    }

    // rowOrigin is the letter at the origin of the set for the
    // column
    let curRow = SET_ORIGIN_ROW_LETTERS.charCodeAt(set - 1);
    let northingValue = 0.0;
    let rewindMarker = false;

    while (curRow !== n.charCodeAt(0)) {
        curRow++;
        if (curRow === I) {
            curRow++;
        }
        if (curRow === O) {
            curRow++;
        }
        // fixing a bug making whole application hang in this loop
        // when 'n' is a wrong character
        if (curRow > V) {
            if (rewindMarker) {
                // making sure that this loop ends
                throw new Error(`Bad character: ${n}`);
            }
            curRow = A;
            rewindMarker = true;
        }
        northingValue += 100000.0;
    }

    return northingValue;
}

/**
 * The function getMinNorthing returns the minimum northing value of a MGRS
 * zone. Ported from Geotrans' c Latitude_Band_Value structure table.

 * @private
 * @param {char} zoneLetter The MGRS zone to get the min northing for.
 * @return {number}
 */
function getMinNorthing(zoneLetter: string) {
    switch (zoneLetter) {
        case 'C':
            return 1100000.0;
        case 'D':
            return 2000000.0;
        case 'E':
            return 2800000.0;
        case 'F':
            return 3700000.0;
        case 'G':
            return 4600000.0;
        case 'H':
            return 5500000.0;
        case 'J':
            return 6400000.0;
        case 'K':
            return 7300000.0;
        case 'L':
            return 8200000.0;
        case 'M':
            return 9100000.0;
        case 'N':
            return 0.0;
        case 'P':
            return 800000.0;
        case 'Q':
            return 1700000.0;
        case 'R':
            return 2600000.0;
        case 'S':
            return 3500000.0;
        case 'T':
            return 4400000.0;
        case 'U':
            return 5300000.0;
        case 'V':
            return 6200000.0;
        case 'W':
            return 7000000.0;
        case 'X':
            return 7900000.0;
        default:
            throw new Error(`Invalid zone letter: ${zoneLetter}`);
    }
}

// Convert lng/lat to MGRS position
export function toMGRS(coord: GeoJSON.Position, accuracy = 5) {
    return encode(toUTM(coord), accuracy).substr(0, accuracy);
}

// Convert MGRS string to lng/lat
export function fromMGRS(mgrs: string) {
    const bbox = fromUTM(decode(mgrs.toUpperCase()));
    if (bbox.length !== 0) {
        return [bbox[0], bbox[1], bbox[0], bbox[1]];
    }
    //return [bbox.left, bbox.bottom, bbox.right, bbox.top];
}
